/**
 * assets/js/frontend/in-view-observer.js
 * 
 * This script is used to help with animations and other effects when elements scroll into view.
 * 
 * Sample usage:
 * 
 * init( 'h1.has-animation, h2.has-animation' ); 
 * init( '.wp-block-gallery.css-fade-in', { once: true } );
 * 
 * Matched elements receive the class 'is-in-view' when they scroll into view.
 * With `once: true`, the class is not removed when scrolling away and the element is unobserved.
 * 
 * @author jjarolim
 */

const LOG_PREFIX = '[in-view-observer]';

/**
 * @param {string} selector
 * @param {{ once?: boolean, debug?: boolean, onIntersect?: (element: Element) => void }} [options]
 */
const init = ( selector, options = {} ) => {

    const { once = false, debug = false, onIntersect } = options;

    const log = ( ...args ) => {
        if ( debug ) {
            console.log( LOG_PREFIX, ...args );
        }
    };

    const warn = ( ...args ) => {
        if ( debug ) {
            console.warn( LOG_PREFIX, ...args );
        }
    };

    const animatedItems = document.querySelectorAll( selector );

    log( 'init', { selector, once, matchCount: animatedItems.length } );

    if ( animatedItems.length === 0 ) {
        warn( 'Keine Elemente für Selector gefunden.' );

        const cssFadeIn = document.querySelectorAll( '.css-fade-in' );
        log( 'Fallback: .css-fade-in Treffer:', cssFadeIn.length, [ ...cssFadeIn ].map( el => ( {
            tag: el.tagName,
            className: el.className,
        } ) ) );

        const galleries = document.querySelectorAll( '.wp-block-gallery' );
        log( 'Fallback: .wp-block-gallery Treffer:', galleries.length, [ ...galleries ].map( el => ( {
            className: el.className,
            hasCssFadeIn: el.classList.contains( 'css-fade-in' ),
        } ) ) );
    }

    const describeElement = element => {
        const rect = element.getBoundingClientRect();
        const figures = element.querySelectorAll( ':scope > figure.wp-block-image' );
        const logos = element.querySelectorAll( ':scope > figure.logo, :scope figure.logo' );

        return {
            tag: element.tagName,
            className: element.className,
            isInView: element.classList.contains( 'is-in-view' ),
            size: { width: rect.width, height: rect.height, top: rect.top, left: rect.left },
            directWpBlockImages: figures.length,
            logoFigures: logos.length,
        };
    };

    const observer = new IntersectionObserver( 
        entries => {
            entries.forEach( 
                entry => {
                    log( 'intersection', {
                        isIntersecting: entry.isIntersecting,
                        intersectionRatio: entry.intersectionRatio,
                        ...describeElement( entry.target ),
                    } );

                    if (entry.isIntersecting) {
                        entry.target.classList.add( 'is-in-view' );
                        log( 'is-in-view gesetzt', entry.target.className );
                        onIntersect?.( entry.target );

                        if ( once ) {
                            observer.unobserve( entry.target );
                            log( 'unobserve (once)', entry.target.className );
                        }
                    } else if ( ! once ) {
                        entry.target.classList.remove( 'is-in-view' );
                        log( 'is-in-view entfernt', entry.target.className );
                    }
                }
            )
        }, 
        {
            rootMargin: '0px 0px 0px 0px',
            threshold: 0
        }
    );

    animatedItems.forEach( ( element, index ) => {
        const info = describeElement( element );
        log( `observe #${ index }`, info );

        if ( info.size.height === 0 && info.size.width === 0 ) {
            warn( `observe #${ index }: Element hat keine sichtbare Größe (0×0) – Observer feuert ggf. nicht.` );
        }

        if ( info.directWpBlockImages === 0 ) {
            warn( `observe #${ index }: Keine direkten Kinder figure.wp-block-image – Stagger/Delay greift nicht.` );
        }

        observer.observe( element );
    } );
}

export { init }